import styled from "styled-components/macro";
import { useState } from "react";
import Overlay, { OverlayTrigger } from "./Overlay";
import {
  BoxHeader,
  BoxContent,
  BoxTitle,
  BoxNavRight,
  BoxNavButton,
  BoxFooter,
} from "./Box";
import { Close } from "./Icons";
import Button from "./Button";
import Stack from "./Stack";
import Composer from "./Composer";
import { useSkipioApi } from "../api";
import { useFields } from "../hooks/use-fields";
import { useToast } from "../hooks/use-toast";
import { useSkipioQuery } from "../hooks/use-query";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import formatPhoneNumber from "../util/formatPhoneNumber";
import { fetchUser } from "../api/users";

export default function AutoReplyOverlay({
  hideProfileOverlay,
  trigger,
}: {
  trigger: OverlayTrigger;
  hideProfileOverlay: () => void;
}) {
  const [errors, setErrors] = useState<Record<string, any>>({});
  const skipioApi = useSkipioApi();
  const queryClient = useQueryClient();
  const user = useQuery({
    queryKey: ["users", "me"],
    queryFn: fetchUser,
    select: (queryData) => queryData.data,
  }).data!;
  const providerPhoneNumber = useSkipioQuery({
    queryKey: ["providerPhoneNumbers", user.providerPhoneNumbers[0].id],
    enabled: user.providerPhoneNumbers.length > 0,
  }).data;

  const { field, value, checkpoint } = useFields({
    initialValue: {
      autoResponder: {
        body: providerPhoneNumber.autoResponderText || "",
      },
    },
  });

  const showToast = useToast();

  async function toggleAutoReply(onSuccess: () => void) {
    setErrors({});
    try {
      await skipioApi.providerPhoneNumbers[providerPhoneNumber.id].put(
        {},
        {
          data: {
            providerPhoneNumber: {
              autoResponderText: value.autoResponder.body,
              autoResponderEnabled: !providerPhoneNumber.autoResponderEnabled,
            },
          },
        }
      );
      showToast(
        `Auto-Reply ${
          providerPhoneNumber.autoResponderEnabled ? "disabled" : "enabled"
        }`,
        {
          success: true,
        }
      );
      queryClient.invalidateQueries({ queryKey: ["providerPhoneNumbers"] });
      checkpoint();
      onSuccess();
      hideProfileOverlay();
    } catch (e) {
      try {
        setErrors(e.response.data.errors);
      } catch (_) {
        throw e;
      }
    }
  }

  if (!providerPhoneNumber) return null;

  return (
    <Overlay
      border
      trigger={trigger}
      content={({ hide }) => (
        <div style={{ width: "500px" }}>
          <BoxHeader stacked white>
            <BoxTitle>Auto-Reply</BoxTitle>
            <BoxNavRight>
              <BoxNavButton onClick={hide}>
                <Close />
              </BoxNavButton>
            </BoxNavRight>
          </BoxHeader>
          <SolePropMessage>
            <Stack>
              {providerPhoneNumber.autoResponderEnabled ? (
                <Stack>
                  <p>
                    Auto-Reply is <strong>enabled.</strong>
                  </p>
                  <p>
                    All incoming messages to{" "}
                    <strong>
                      {formatPhoneNumber(providerPhoneNumber.phoneNumber)}
                    </strong>{" "}
                    will be automatically responded to with the following
                    message:
                  </p>
                </Stack>
              ) : (
                <p>
                  While enabled, all incoming messages to{" "}
                  <strong>
                    {formatPhoneNumber(providerPhoneNumber.phoneNumber)}
                  </strong>{" "}
                  will be automatically responded to with the following message:
                </p>
              )}
              <Composer
                {...field("autoResponder")}
                disabled={providerPhoneNumber.autoResponderEnabled}
                enableRinglessVoicemail={false}
                enableMedia={false}
                enableScheduling={false}
                error={errors.autoResponderText}
              />
              {providerPhoneNumber.autoResponderEnabled && (
                <p>
                  <em>
                    You must disable auto-reply before editing your message.
                  </em>
                </p>
              )}
              <div style={{ height: "10px" }} />
            </Stack>
          </SolePropMessage>
          <BoxFooter>
            <BoxNavRight>
              <Button
                onClick={() => {
                  toggleAutoReply(hide);
                }}
              >
                {providerPhoneNumber.autoResponderEnabled
                  ? "Disable Auto-Reply"
                  : "Enable Auto-Reply"}
              </Button>
            </BoxNavRight>
          </BoxFooter>
        </div>
      )}
    />
  );
}

const SolePropMessage = styled(BoxContent)`
  min-width: 320px;
  max-width: 500px;
  line-height: 1.5em;
  strong {
    font-weight: bold;
  }
  em {
    font-style: italic;
  }
`;
